import {
  Bell,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  Home,
  MapPin,
  Search,
  Star,
  Store,
  TrendingUp,
  User,
  Wallet,
} from "lucide-react";

const StatusBar = ({ dark = false }) => (
  <div
    className={`flex items-center justify-between px-6 pt-4 text-[10px] font-semibold ${
      dark ? "text-white" : "text-ink"
    }`}
  >
    <span>9:41</span>
    <div className="flex items-center gap-1.5">
      <span className="h-1.5 w-3 rounded-sm bg-current opacity-70" />
      <span className="h-1.5 w-3 rounded-sm bg-current opacity-70" />
      <span className="h-2 w-4 rounded-sm border border-current opacity-70" />
    </div>
  </div>
);

const CustomerApp = () => (
  <div className="flex h-full flex-col bg-mist">
    <div className="bg-gradient-to-br from-navy to-royal px-5 pb-6">
      <StatusBar dark />
      <div className="mt-4 flex items-center gap-2">
        <img src="/assets/craftsi-logo-white.png" alt="" className="h-6 w-auto" />
        <span className="font-heading text-sm font-bold tracking-tight text-white">craftsi</span>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/60">Good morning</p>
          <p className="font-heading text-base font-bold text-white">Find services near you</p>
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
          <Bell className="h-4 w-4 text-white" />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-1 text-[11px] text-white/80">
        <MapPin className="h-3 w-3 text-gold" />
        Your City
        <ChevronRight className="h-3 w-3" />
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-[11px] text-fog shadow-md">
        <Search className="h-3.5 w-3.5 text-royal" />
        Search plumbers, salons, tutors…
      </div>
    </div>

    <div className="flex gap-2 overflow-hidden px-5 pt-4">
      {["Home", "Beauty", "Health", "Auto"].map((c, i) => (
        <span
          key={c}
          className={`rounded-full px-3.5 py-1.5 text-[10px] font-semibold ${
            i === 0 ? "bg-navy text-white" : "border border-gray-200 bg-white text-ink"
          }`}
        >
          {c}
        </span>
      ))}
    </div>

    <div className="mt-4 flex-1 space-y-3 px-5">
      {[
        { name: "Home Deep Cleaning", shop: "Sparkle Home Care", worker: "Ravi Kumar", rating: "4.8" },
        { name: "AC Repair & Service", shop: "CoolAir Services", worker: "Anil Verma", rating: "4.7" },
        { name: "Salon at Home", shop: "Glamour Studio", worker: "Priya Sharma", rating: "4.9" },
      ].map((s) => (
        <div key={s.name} className="flex items-center gap-2.5 rounded-2xl bg-white p-2.5 shadow-sm">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-navy-50">
            <Store className="h-4.5 w-4.5 text-royal" />
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-[11px] font-semibold text-ink">{s.shop}</p>
            <p className="truncate text-[9px] font-medium text-royal">{s.name}</p>
            <p className="truncate text-[9px] text-fog">Vendor: {s.worker}</p>
          </div>
          <span className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-gold/10 px-2 py-1 text-[9px] font-bold text-gold-dark">
            <Star className="h-2.5 w-2.5 fill-gold text-gold" />
            {s.rating}
          </span>
        </div>
      ))}
    </div>

    <div className="flex items-center justify-around border-t border-gray-100 bg-white px-6 py-3">
      {[Home, Search, CalendarDays, User].map((Icon, i) => (
        <Icon key={i} className={`h-5 w-5 ${i === 0 ? "text-royal" : "text-gray-300"}`} />
      ))}
    </div>
  </div>
);

const VendorApp = () => (
  <div className="flex h-full flex-col bg-mist">
    <StatusBar />
    <div className="flex items-center justify-between px-5 pt-3">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-fog">Craftsi Business</p>
        <p className="font-heading text-base font-bold text-ink">Vendor Dashboard</p>
      </div>
      <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-white shadow-sm">
        <img src="/assets/craftsi-logo.png" alt="" className="h-5 w-auto" />
      </div>
    </div>

    <div className="mx-5 mt-4 rounded-2xl bg-navy p-4 text-white shadow-lg shadow-navy/25">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-white/60">Earnings this week</p>
        <Wallet className="h-4 w-4 text-gold" />
      </div>
      <p className="mt-1 font-heading text-2xl font-extrabold">₹12,400</p>
      <p className="mt-1 flex items-center gap-1 text-[10px] text-emerald-300">
        <TrendingUp className="h-3 w-3" /> +18% vs last week
      </p>
    </div>

    <div className="mx-5 mt-4 grid grid-cols-3 gap-2">
      {[
        { label: "Bookings", value: "32" },
        { label: "Profile Views", value: "1.2k" },
        { label: "Rating", value: "4.8" },
      ].map((s) => (
        <div key={s.label} className="rounded-xl bg-white p-3 text-center shadow-sm">
          <p className="font-heading text-sm font-bold text-navy">{s.value}</p>
          <p className="mt-0.5 text-[9px] text-fog">{s.label}</p>
        </div>
      ))}
    </div>

    <div className="mt-4 flex-1 space-y-2.5 px-5">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-fog">
        Today's bookings
      </p>
      {[
        { name: "Home Cleaning — 10:00 AM", customer: "Ananya R.", value: "₹1,499", status: "Confirmed", ok: true },
        { name: "Kitchen Deep Clean — 2:30 PM", customer: "Vikram S.", value: "₹2,199", status: "Pending", ok: false },
        { name: "Sofa Cleaning — 5:00 PM", customer: "Meera K.", value: "₹999", status: "Confirmed", ok: true },
      ].map((b) => (
        <div key={b.name} className="flex items-center gap-2.5 rounded-xl bg-white p-2.5 shadow-sm">
          <ClipboardList className="h-4 w-4 shrink-0 text-royal" />
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-[10px] font-medium text-ink">{b.name}</p>
            <p className="truncate text-[9px] text-fog">Customer: {b.customer}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-0.5">
            <span className="text-[10px] font-bold text-navy">{b.value}</span>
            <span
              className={`rounded-full px-2 py-0.5 text-[8px] font-bold ${
                b.ok ? "bg-emerald-50 text-emerald-600" : "bg-gold/10 text-gold-dark"
              }`}
            >
              {b.status}
            </span>
          </div>
        </div>
      ))}
    </div>

    <div className="flex items-center justify-around border-t border-gray-100 bg-white px-6 py-3">
      {[Home, ClipboardList, TrendingUp, User].map((Icon, i) => (
        <Icon key={i} className={`h-5 w-5 ${i === 0 ? "text-royal" : "text-gray-300"}`} />
      ))}
    </div>
  </div>
);

const PhoneMockup = ({ variant = "customer", className = "" }) => (
  <div
    data-testid={`${variant}-phone-mockup`}
    className={`relative w-[270px] rounded-[3rem] bg-[#0B1526] p-[10px] shadow-[0_50px_100px_-30px_rgba(11,42,91,0.5)] sm:w-[295px] ${className}`}
  >
    <div className="absolute left-1/2 top-[20px] z-20 h-[22px] w-24 -translate-x-1/2 rounded-full bg-[#0B1526]" />
    <div className="relative h-[540px] overflow-hidden rounded-[2.4rem]">
      {variant === "customer" ? <CustomerApp /> : <VendorApp />}
    </div>
  </div>
);

export default PhoneMockup;
